import React from 'react';
import { useContent } from '../../hooks/useContent';
import { uploadsAbsoluteUrl } from '../../utils/uploadsBase';
import styles from './AboutConference.module.css';

const AboutConference = () => {
  const { t } = useContent();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const img = (key, fallback) => {
    const path = t(key, fallback);
    if (!path) return fallback;
    const s = String(path);
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/uploads')) return uploadsAbsoluteUrl(s, API_URL);
    return s || fallback;
  };

  return (
    <div className={styles.container}>
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}>
          <h1 className={styles.heroTitle}>
            {t('aboutConference.hero.title', 'International Scientific Conference')}
          </h1>
          <p className={styles.heroSubtitle}>
            {t('aboutConference.hero.subtitle', 'AdvAES 2025 | Tashkent, Uzbekistan')}
          </p>
          <p className={styles.heroDate}>
            {t('aboutConference.hero.date', 'June 12-13, 2025')}
          </p>
        </div>
      </section>

      <section className={styles.welcomeSection}>
        <div className={styles.welcomeContent}>
          <h2 className={styles.sectionTitle}>
            {t(
              'aboutConference.welcome.title',
              'Join Us for a Pioneering Scientific Exchange!',
            )}
          </h2>
          <p className={styles.welcomeText}>
            {t(
              'aboutConference.welcome.text',
              'Embrace the opportunity to contribute to cutting-edge research and connect with global experts. In June 2025, Tashkent, Uzbekistan, will become a hub for scientists and researchers from around the world. We invite you to participate in our prestigious International Scientific Conference, dedicated to the most pressing issues in Advances in Applied Engineering Sciences (AdvAES 2025).',
            )}
          </p>
        </div>
      </section>

      <section className={styles.formatsSection}>
        <h2 className={styles.sectionTitle}>
          {t('aboutConference.formats.title', 'Two Participation Formats')}
        </h2>

        <div className={styles.formatsGrid}>
          <div className={styles.formatCard}>
            <div className={styles.formatHeader}>
              <svg className={styles.formatIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h3 className={styles.formatTitle}>
                {t(
                  'aboutConference.formats.inPerson.title',
                  'In-Person Conference Experience',
                )}
              </h3>
            </div>

            <p className={styles.formatDescription}>
              {t(
                'aboutConference.formats.inPerson.description',
                'Immerse yourself in a dynamic academic environment and present your work live in Tashkent.',
              )}
            </p>

            <div className={styles.benefits}>
              <h4 className={styles.benefitsTitle}>
                {t('aboutConference.formats.inPerson.benefitsTitle', 'Benefits:')}
              </h4>
              <ul className={styles.benefitsList}>
                <li>{t('aboutConference.formats.inPerson.benefit1', 'Present Your Research: Showcase your scientific work at the conference')}</li>
                <li>{t('aboutConference.formats.inPerson.benefit2', 'Engage in Live Discussions: Connect with colleagues from various countries')}</li>
                <li>{t('aboutConference.formats.inPerson.benefit3', 'Unforgettable Experience: Immerse yourself in scientific collaboration')}</li>
              </ul>
            </div>

            <div className={styles.dates}>
              <h4 className={styles.datesTitle}>
                {t('aboutConference.formats.inPerson.datesTitle', 'Key Dates:')}
              </h4>
              <div className={styles.datesList}>
                {[
                  ['d1Label', 'd1Value', 'Article Submission Deadline', 'April 14, 2025'],
                  ['d2Label', 'd2Value', 'Notification of Acceptance', 'May 10, 2025'],
                  ['d3Label', 'd3Value', 'Final Article Submission', 'May 16, 2025'],
                  ['d4Label', 'd4Value', 'Registration & Welcome Reception', 'June 12, 2025'],
                  ['d5Label', 'd5Value', 'Conference Presentations', 'June 12-13, 2025'],
                  ['d6Label', 'd6Value', 'Journal Publication', 'June 30, 2025'],
                ].map(([lk, vk, fbL, fbV]) => (
                  <div key={lk} className={styles.dateItem}>
                    <span className={styles.dateLabel}>
                      {t(`aboutConference.formats.inPerson.${lk}`, fbL)}
                    </span>
                    <span className={styles.dateValue}>
                      {t(`aboutConference.formats.inPerson.${vk}`, fbV)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.formatCard}>
            <div className={styles.formatHeader}>
              <svg className={styles.formatIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              <h3 className={styles.formatTitle}>
                {t(
                  'aboutConference.formats.remote.title',
                  'Remote Publication Opportunity',
                )}
              </h3>
            </div>

            <p className={styles.formatDescription}>
              {t(
                'aboutConference.formats.remote.description',
                "Expand your research's reach and impact by publishing in our esteemed scientific journal, without the need for physical attendance.",
              )}
            </p>

            <div className={styles.benefits}>
              <h4 className={styles.benefitsTitle}>
                {t('aboutConference.formats.remote.benefitsTitle', 'Benefits:')}
              </h4>
              <ul className={styles.benefitsList}>
                <li>{t('aboutConference.formats.remote.benefit1', 'Global Visibility: Your research will be accessible to international readership')}</li>
                <li>{t('aboutConference.formats.remote.benefit2', 'Prestigious Publication: Indexed in leading scientific databases')}</li>
                <li>{t('aboutConference.formats.remote.benefit3', 'Flexible Participation: No travel required')}</li>
              </ul>
            </div>

            <div className={styles.dates}>
              <h4 className={styles.datesTitle}>
                {t('aboutConference.formats.remote.datesTitle', 'Key Dates:')}
              </h4>
              <div className={styles.datesList}>
                {[
                  ['d1Label', 'd1Value', 'Article Submission Deadline', 'May 2, 2025'],
                  ['d2Label', 'd2Value', 'Notification of Acceptance', 'May 27, 2025'],
                  ['d3Label', 'd3Value', 'Final Article Submission', 'June 5, 2025'],
                  ['d4Label', 'd4Value', 'Journal Publication', 'June 30, 2025'],
                ].map(([lk, vk, fbL, fbV]) => (
                  <div key={lk} className={styles.dateItem}>
                    <span className={styles.dateLabel}>
                      {t(`aboutConference.formats.remote.${lk}`, fbL)}
                    </span>
                    <span className={styles.dateValue}>
                      {t(`aboutConference.formats.remote.${vk}`, fbV)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.chairsSection}>
        <h2 className={styles.sectionTitle}>
          {t('aboutConference.chairs.title', 'Conference Organization')}
        </h2>
        <div className={styles.chairsGrid}>
          <div className={styles.chairCard}>
            <div className={styles.chairIcon}>💬</div>
            <h3 className={styles.chairTitle}>
              {t('aboutConference.chairs.card1Title', 'Conference Chairs')}
            </h3>
            <p className={styles.chairText}>
              {t('aboutConference.chairs.card1Text', 'Leading experts organizing the conference')}
            </p>
          </div>

          <div className={styles.chairCard}>
            <div className={styles.chairIcon}>📋</div>
            <h3 className={styles.chairTitle}>
              {t('aboutConference.chairs.card2Title', 'Secretary of AdvAES')}
            </h3>
            <p className={styles.chairText}>
              {t('aboutConference.chairs.card2Text', 'Administrative and organizational support')}
            </p>
          </div>
        </div>
      </section>

      <section className={styles.venueSection}>
        <h2 className={styles.sectionTitle}>
          {t('aboutConference.venue.sectionTitle', 'Conference Venue')}
        </h2>
        <div className={styles.venueContent}>
          <div className={styles.venueInfo}>
            <h3 className={styles.venueTitle}>
              {t('aboutConference.venue.title', 'Tashkent, Uzbekistan')}
            </h3>
            <p className={styles.venueDescription}>
              {t(
                'aboutConference.venue.description',
                'The conference will be held at Tashkent University of Applied Sciences, one of the leading educational institutions in Central Asia. The modern facilities and strategic location make it an ideal venue for international scientific exchange.',
              )}
            </p>
            <div className={styles.venueFeatures}>
              {[
                ['feature1', '🏛️', 'Modern Conference Halls'],
                ['feature2', '💻', 'Advanced Technology'],
                ['feature3', '🍽️', 'Catering Services'],
                ['feature4', '🚗', 'Easy Access'],
              ].map(([key, icon, fb]) => (
                <div key={key} className={styles.feature}>
                  <span className={styles.featureIcon}>{icon}</span>
                  <span>{t(`aboutConference.venue.${key}`, fb)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.venueImage}>
            <img
              src={img(
                'aboutConference.venue.image',
                'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600',
              )}
              alt={t('aboutConference.venue.imageAlt', 'Conference venue')}
            />
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>
            {t('aboutConference.cta.title', "Don't Miss This Chance!")}
          </h2>
          <p className={styles.ctaText}>
            {t(
              'aboutConference.cta.text',
              'Be part of a significant scientific event and contribute to advancing applied engineering sciences.',
            )}
          </p>
          <div className={styles.ctaButtons}>
            <button type="button" className={styles.ctaButton}>
              {t('aboutConference.cta.buttonPrimary', 'Register Now')}
            </button>
            <button type="button" className={styles.ctaButtonSecondary}>
              {t('aboutConference.cta.buttonSecondary', 'Submit Article')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutConference;
